import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

export type LegalKind = "terms" | "privacy";

export interface LegalDocumentRow {
  id: string;
  kind: LegalKind;
  version_label: string;
  body: string;
  status: "draft" | "published";
  created_at: string;
  published_at: string | null;
  published_once_at: string | null;
}

/** Where a version stands. A retired one is a `draft` row that was once live (0302). */
export type LegalVersionState = "draft" | "published" | "retired";

export function versionState(row: LegalDocumentRow): LegalVersionState {
  if (row.status === "published") return "published";
  return row.published_once_at ? "retired" : "draft";
}

/** Only a never-published draft can be edited or deleted — 0302's trigger refuses the rest. */
export function isEditable(row: LegalDocumentRow): boolean {
  return versionState(row) === "draft";
}

const legalKey = (kind: LegalKind) => ["legal-documents", kind] as const;

/** Every version of one document, newest first. Superadmin reads all rows (0288 RLS). */
export function useLegalDocuments(kind: LegalKind) {
  return useQuery({
    queryKey: legalKey(kind),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legal_documents")
        .select(
          "id, kind, version_label, body, status, created_at, published_at, published_once_at",
        )
        .eq("kind", kind)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as LegalDocumentRow[];
    },
  });
}

/**
 * Create a draft, or edit one that was never published. A plain RLS write —
 * 0288 grants legal writes to superadmin. Publishing is NOT done here.
 */
export function useSaveLegalDraft(kind: LegalKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string | null; versionLabel: string; body: string }) => {
      const fields = { version_label: input.versionLabel.trim(), body: input.body };
      const query = input.id
        ? supabase.from("legal_documents").update(fields).eq("id", input.id)
        : supabase.from("legal_documents").insert({ ...fields, kind });
      const { data, error } = await query.select("id").single();
      if (error) {
        if (error.code === "23505") throw new Error("That version label is already used.");
        throw error;
      }
      return data.id as string;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: legalKey(kind) }),
  });
}

/** Publish through the admin function (service role, audited, retires the live version). */
export function usePublishLegalDocument(kind: LegalKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      callEdgeFunction("admin/legal-documents/publish", { data: { id: documentId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: legalKey(kind) }),
  });
}
